import Groups from 'track/Groups.js';
import SubPatchLoader from "zobject/SubPatchLoader.js";
import RollableObject from "track/RollableObject.js"
import LostParameters from "utils/LostParameters.js";
import Colorable from "engine/Colorable.js";
import Dispatchable from "utils/Dispatchable.js";
import PlayheadEffect from "effects/live/PlayheadEffect.js";
import ParameterContext from 'effects/ParameterContext.js';
import StepData from "pattern/StepData.js";
import MidiEffectsChain from 'effects/midi/MidiEffectsChain.js';
import {uuid} from 'uuidv4';
import EnvelopesTrack from 'track/EnvelopesTrack.js';
import MasterStepManager from './MasterStepManager.js';
import SubSequenceMidiEffects from "effects/midi/SubSequenceMidiEffects.js";
import LFOsTrack from 'track/LFOsTrack.js';
import Mix from 'session/Mix.js';
import Paddable from './pads/Paddable.js';
import {
    setSubSequenceFade,
    setSubSequenceMuted,
    setArrangementAutomationToShow,
} from 'actions/index.js';
import {
    PATTERN_SWITCH,
    SUB_SEQUENCE_VOLUME_CHANGE
} from 'session/constants.js';
import Sequenceable from "engine/Sequenceable.js";
import ZObjects from 'zobject/ZObjects.js';
import ZObjectBuilder from "zobject/ZObjectBuilder.js";

var id = 0;

const NAMES = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', "J", "K", "L", "M", "N"];

class SubSequence extends RollableObject(SubPatchLoader(ZObjects(ZObjectBuilder(LostParameters(Colorable(Dispatchable(Sequenceable(Paddable(class {}))))))))) {
    constructor(sequencerEngine, isTrack=false) {
        super();
        this.sequencerEngine = sequencerEngine;
        this.store = sequencerEngine.store;
        this.isTrack = isTrack; 
        this.id = uuid();
        
        this.groups = new Groups(
            sequencerEngine.channelMixer,
            sequencerEngine.sends,
            sequencerEngine,
            this);
        this.lfosTrack = new LFOsTrack(sequencerEngine, this);
        this.envelopesTrack = new EnvelopesTrack(sequencerEngine, this);

        this.tracks = [];
        this.mix = new Mix(sequencerEngine, this);
        this.setAutomationToShow(PATTERN_SWITCH);
        this.midiEffects = new SubSequenceMidiEffects(this, sequencerEngine);
        this.playhead = new PlayheadEffect(this);
        this.playhead.registerContext(new ParameterContext().forSubSequence(this));

        this.subSequence = this;
        this.trackData = new StepData(
            sequencerEngine,
            this.mix,
            new ParameterContext().forSubSequence(this));
        this.trackData.loadDefaults();
        this.initColor();
        this.higherRolling = undefined;

        this.initPattr();
    }

    get state() {
        return this.sequencerEngine.state;
    }

    getEffectNumber(effect, isMidi=false) {
        if (isMidi) {
            return this.midiEffects.getEffectNumber(effect);
        }
        return 0;
    }


    moveMidiEffectTo(from, to) {
        this.midiEffects.moveEffectTo(from, to);
    }

    get name() {
        let i = this.sequencerEngine.subSequences.indexOf(this);
        return NAMES[i];
    }

    linearRampFade(fromValue, toValue, time) {
        this.tracks.forEach(
            track => track.linearRampFade(fromValue, toValue, time));
    }

    set fade(x) {
        x = x || 0;

        if (this.fadeVolume === x) {
            return;
        }

        if (!this.muted) {
            this.tracks.forEach(
                track  => track.fade = x);
        }

        this.store.dispatch(setSubSequenceFade(
            this.id,
            x));
    }

    set mute(muted) {
        let volume = muted ? 0 : this.fadeVolume;
        this.tracks.forEach(
            track => track.fade = volume);

        this.store.dispatch(setSubSequenceMuted(
            this.id, muted));
    }

    disposeParameters() {
        this.lfos.forEach(lfo => lfo.lfo.disposeParameters());
        this.envelopes.forEach(envelope => envelope.disposeParameters());
    }

    dispose() {
        this.pads.forEach(p => p.deletePad());
        this.tracks.forEach(x => x.dispose());
        this.groups.dispose();
        this.tracks = undefined;
        this.groups = undefined;
        this.envelopes.forEach(envelope => envelope.dispose());
        this.lfos.forEach(lfo => lfo.lfo.dispose());
        this.lfosTrack = undefined;
        this.envelopesTrack = undefined;
 
    }

    addTrack(track) {
        track.subSequence = this;
        this.tracks.push(track);
        if (this.isTrack) {
            this.groups.addTrack(track);
        }
    }

    setId(newId) {
        this.id = newId;
        id = newId + 1;
    }

    get channelMixer() {
        return this.sequencerEngine.channelMixer;
    }

    get sends() {
        return this.sequencerEngine.sends;
    }

    get lfos() {
        return this.lfosTrack.lfos;
    }

    get length() {
        let max = -1;
        for (let track of this.tracks) {
            let len = track.length;
            if (len >= max) {
                max = len;
            }
        }
        return max;
    }

    get envelopes() {
        return this.envelopesTrack.envelopes.map(
            e => e.envelope);
    }

    get fadeVolume() {
        return this.store.getState().mix.subSequenceFades[this.id] || 1;
    }

    setAutomationToShow(type) {
        this.store.dispatch(setArrangementAutomationToShow(this.id, type));
    }

    get volumeAutomationMix() {
        return this.mix.mixesByType[SUB_SEQUENCE_VOLUME_CHANGE];
    }

    get loopStart() {
        return this.store.getState().arrangement.selection.start;
    }

    get looping() {
        return this.store.getState().arrangement.selection.start;
    }

    updateVolumeFromAutomation(step) {
        let updatedVolume =
            this.volumeAutomationMix.getValueAtStep(step, this.fadeVolume);

        if (this.looping && step === this.loopStart) {
            this.mix.restart();
            this.fade = updatedVolume;
        } else if (this.fadeVolume !== updatedVolume) {
            this.store.dispatch(setSubSequenceFade(
                this.id,
                updatedVolume));
        }
    }

    getJson() {
        return {
            id: this.id,
            isTrack: this.isTrack,
            playhead: this.playhead.getJson(),
            color: this.color,
            pattr: this.pattr.getJson()
        };
    }

    fromJson(json) {
        this.id = json.id;
        if (json.isTrack !== undefined) {
            this.isTrack = json.isTrack;
        }
        if (json.playhead) {
            this.playhead.fromJson(json.playhead);
        }
        if (json.color) {
            this.color = json.color;
        } else {
            this.initColor();
        }
        this.pattr.fromJson(json.pattr);
    }

    getAllParameters() {
        return [
            ... this.playhead.parameters,
            ... this.midiEffects.getAllParameters(),
            ... this.pads.flatMap(pad => pad.getAllParameters())
        ];
    }

    getAllEffects() {
        return [
            ...this.midiEffects.effects,
            ...this.pads.flatMap(p => p.effectRack === undefined ? [] : p.effectRack.effects)];
    }

    newPattern() {
        if (this.tracks.length === 0) {
            return;
        }
        let newPatternNumber = this.tracks[0].patterns.length;
        let oldPatternNumber = this.tracks[0].currentPattern;
        let effects = this.getAllEffects();
        effects.forEach(
            e => e.locked && e.lockPattern(newPatternNumber, true));
        let parameters = effects.flatMap(e => e.getParameters());

        // get the pad effects volumes
        let fxVolumes = this.pads.map(
            x => x.fxMacro.macroParameter.get());

        parameters
            .forEach(parameter => parameter.newPattern(oldPatternNumber, newPatternNumber));


        this.pads.forEach(
            (x, i) => x.fxMacro.macroParameter.set(fxVolumes[i]));
    }


    insideInstance() {
        return false;
    }

    propagate() {
    }
}

export default SubSequence;
